import { Link } from '@tanstack/react-router';
import IconButton from '../core/IconButton';
import { useStatusOptions } from '@/hooks/useStatusOptions';
import { cn } from '@/helpers/classnames';
import { IconCreditCard, IconEyeArrow, IconUserGroup } from '@/icons';
import { OrganizationDTO } from '@/api/trafikportalen';
import { useTenantID } from '@/hooks/useTenantID';
import { useImpersonation } from '@/hooks/useImpersonation';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getOrganizationEffectiveState,
  organizationHasStudentAccess,
} from '@/helpers/organizationStatus';
import { UserProfileDTO } from '@/api/auth';

interface OrganizationListItemProps {
  organization: OrganizationDTO;
  profiles?: UserProfileDTO[];
}

export const OrganizationListItem = ({
  organization,
  profiles,
}: OrganizationListItemProps) => {
  const tenantId = useTenantID();
  const getStatus = useStatusOptions();
  const impersonate = useImpersonation({ profiles });
  const { formatMessage, locale } = useIntl();

  const effectiveState = getOrganizationEffectiveState(organization);
  const status = getStatus({ status: effectiveState });
  const hasStudentAccess = organizationHasStudentAccess(organization);

  const { id, name, organization_number, credit } = organization;

  return (
    <div className="relative flex @container">
      <Link
        to="/$tenantId/organizations/organization/$organizationId"
        params={{ tenantId, organizationId: id }}
        className="flex w-full flex-wrap items-center rounded-md border border-background-300 bg-background-50 px-4 py-2 hover:bg-background-200 @2xl:grid @2xl:grid-cols-12"
      >
        <div className="flex w-full flex-col @2xl:col-span-8 @2xl:w-auto">
          <span className="truncate pr-6 font-medium">{name}</span>
          <span>{organization_number}</span>
        </div>
        <div className="flex w-full flex-row items-center @2xl:col-span-4 @2xl:my-0">
          <div className="flex w-full flex-col text-nowrap">
            <div
              className="flex w-full flex-row items-center gap-1 @2xl:pr-2"
              title={formatMessage({ defaultMessage: 'Kreditgräns' })}
            >
              <IconCreditCard className="size-4 text-text-700" />
              <span className="text-text-700">
                {credit === undefined ? (
                  <FormattedMessage defaultMessage="Ingen kreditgräns" />
                ) : (
                  `${credit.toLocaleString(locale)} SEK`
                )}
              </span>
            </div>
            {hasStudentAccess && (
              <span className="mt-1 flex items-center gap-1 text-text-700">
                <IconUserGroup className="size-4 shrink-0" aria-hidden />
                <FormattedMessage defaultMessage="Barn & ungdomar" />
              </span>
            )}
          </div>
        </div>
      </Link>
      {organization.state === 'approved' &&
        effectiveState !== 'inactivating' && (
          <IconButton
            aria-label={formatMessage({
              defaultMessage: 'Visa som organisation',
            })}
            icon={<IconEyeArrow />}
            className="absolute bottom-4 right-4 flex hover:bg-primary-600 hover:text-contrast-primary @2xl:bottom-1/2 @2xl:translate-y-1/2"
            onClick={() => impersonate(id)}
            title={formatMessage({ defaultMessage: 'Visa som organisation' })}
          />
        )}
      <div
        className={cn(
          'absolute right-4 top-4 flex size-3 flex-row rounded-full @2xl:right-16 @2xl:top-1/2 @2xl:-translate-y-1/2',
          status ? status.style : 'bg-background-500',
        )}
      />
    </div>
  );
};
